import { useCallback } from 'react';
import { addChipsToUrlParams } from 'helpers/chipSerialization';

/**
 * Custom hook for filter persistence (localStorage + URL)
 * Handles saving, clearing, and loading filter values with configurable persistence options
 * 
 * @param {string} storageKey - Key for localStorage
 * @param {string} siteAppId - Site application ID
 * @param {Object} config - Configuration object with persistence options
 * @returns {Object} Persistence methods and config
 */
export const useFilterPersistence = (storageKey, siteAppId, config) => {
  const persistenceConfig = {
    useLocalStorage: !!storageKey && !!siteAppId,
    useUrlParams: true, // Always use URL params by default
    ...config?.persistence
  };

  const getStorageKey = useCallback(() => {
    return `filter-${storageKey}-${siteAppId}`;
  }, [storageKey, siteAppId]);

  const saveFilter = useCallback((filterType, filterId) => {
    if (persistenceConfig.useLocalStorage && storageKey && siteAppId) {
      try {
        const key = getStorageKey();
        const existingFilters = JSON.parse(localStorage.getItem(key) || '{}');
        existingFilters[filterType] = filterId;
        localStorage.setItem(key, JSON.stringify(existingFilters));
      } catch (error) {
        console.warn('Failed to save filter to localStorage:', error);
      }
    }
  }, [persistenceConfig.useLocalStorage, storageKey, siteAppId, getStorageKey]);

  const clearFilter = useCallback((filterType) => {
    if (persistenceConfig.useLocalStorage && storageKey && siteAppId) {
      try {
        const key = getStorageKey();
        const existingFilters = JSON.parse(localStorage.getItem(key) || '{}');
        delete existingFilters[filterType];
        localStorage.setItem(key, JSON.stringify(existingFilters));
      } catch (error) {
        console.warn('Failed to clear filter from localStorage:', error);
      }
    }
  }, [persistenceConfig.useLocalStorage, storageKey, siteAppId, getStorageKey]);

  const loadFilters = useCallback(() => {
    if (!persistenceConfig.useLocalStorage || !storageKey || !siteAppId) return {};
    
    try {
      const key = getStorageKey();
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to load filters from localStorage:', error);
      return {};
    }
  }, [persistenceConfig.useLocalStorage, storageKey, siteAppId, getStorageKey]);

  const updateUrlAndStorage = useCallback((filterValues, currentSiteAppId, filterPersistSettings = {}) => {
    if (!persistenceConfig.useUrlParams) return;

    const urlParams = new URLSearchParams(window.location.search);
    
    // Always preserve siteAppId if it exists
    if (currentSiteAppId) {
      urlParams.set('siteAppId', currentSiteAppId);
    }
    
    // Update URL parameters and storage for each filter
    Object.entries(filterValues).forEach(([filterType, filterData]) => {
      if (filterType === 'chips') {
        // Handle chips URL serialization, but filter out hidden types
        const chips = filterData?.value;
        
        if (Array.isArray(chips) && chips.length > 0) {
          // Get hiddenTypes from config to filter out chips that are handled by dropdown filters
          const hiddenTypes = config?.filters?.chips?.hiddenTypes || [];
          const visibleChips = chips.filter(chip => !hiddenTypes.includes(chip.chipType));
          
          if (visibleChips.length > 0) {
            addChipsToUrlParams(urlParams, visibleChips);
            saveFilter(filterType, visibleChips); // Only save visible chips
          } else {
            urlParams.delete('chip');
            clearFilter(filterType);
          }
        } else {
          urlParams.delete('chip');
          clearFilter(filterType);
        }
      } else if (config?.filters?.[filterType]?.multiSelect && Array.isArray(filterData?.value)) {
        // Handle multi-select filters (array of objects with id property)
        const items = filterData.value;

        if (items.length > 0) {
          // Check if Show All is selected (empty id or 'all')
          const hasShowAll = items.some(item => item.id === '' || item.id === 'all' || (item.id === '' && item.name === 'Show All'));
          
          if (hasShowAll) {
            // Check if this filter has persistAll=true
            const shouldPersistAll = filterPersistSettings[filterType] || false;
            
            if (shouldPersistAll) {
              // Save 'all' to both URL and localStorage for filters with persistAll=true
              urlParams.set(filterType, 'all');
              saveFilter(filterType, 'all');
            } else {
              // For other Show All filters, remove from URL and localStorage
              urlParams.delete(filterType);
              clearFilter(filterType);
            }
          } else {
            // Remove existing parameters for this filter
            urlParams.delete(filterType);

            // Add multiple parameters for this filter
            items.forEach(item => {
              if (item.id && item.id !== '') {
                urlParams.append(filterType, item.id);
              }
            });

            // Save item IDs to localStorage
            const itemIds = items.map(item => item.id).filter(id => id !== '');
            saveFilter(filterType, itemIds);
          }
        } else {
          // Empty array means "Show All" - remove parameters
          urlParams.delete(filterType);
          clearFilter(filterType);
        }
      } else if (filterData?.value?.id !== undefined) {
        // Check if this is the "Show All" option
        if (filterData.value.id === '' && filterData.value.name === 'Show All') {
          // Check if this filter has persistAll=true
          const shouldPersistAll = filterPersistSettings[filterType] || false;

          if (shouldPersistAll) {
            // Save 'all' to both URL and localStorage for filters with persistAll=true
            urlParams.set(filterType, 'all');
            saveFilter(filterType, 'all');
          } else {
            // For other Show All filters, remove from URL and localStorage
            urlParams.delete(filterType);
            clearFilter(filterType);
          }
        } else if (filterData.value.id !== '') {
          // For normal non-empty ids (including custom date ranges with serialized format)
          urlParams.set(filterType, filterData.value.id);
          saveFilter(filterType, filterData.value.id);
        } else {
          // Remove from both URL and storage when filter is cleared (empty id for non-show-all filters)
          urlParams.delete(filterType);
          clearFilter(filterType);
        }
      } else {
        // Remove from both URL and storage when filter is cleared
        urlParams.delete(filterType);
        clearFilter(filterType);
      }
    });
    
    // Update the URL without causing a page reload
    const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.pushState({}, '', newUrl);
    }
  }, [persistenceConfig.useUrlParams, saveFilter, clearFilter, getStorageKey, storageKey, persistenceConfig.useLocalStorage, config]);

  const getInitialValues = useCallback((config) => {
    if (!config?.filters) return {};
    
    const urlParams = new URLSearchParams(window.location.search);
    const initValues = {};
    
    // Check if any CONFIGURED filter parameters are present in URL
    const configuredFilterTypes = Object.keys(config.filters);
    const hasUrlFilterParams = configuredFilterTypes.some(filterType => {
      // Special case: chips use 'chip' (singular) in URL but 'chips' (plural) in config
      if (filterType === 'chips') {
        return urlParams.has ? urlParams.has('chip') : urlParams.get('chip') !== null;
      }
      return urlParams.has ? urlParams.has(filterType) : urlParams.get(filterType) !== null;
    });
    
    // Load stored filters from localStorage
    const storedFilters = loadFilters();
    
    if (hasUrlFilterParams) {
      // URL has filter parameters - use URL values where available, localStorage as fallback
      Object.keys(config.filters).forEach(filterType => {
        if (config?.filters?.[filterType]?.multiSelect) {
          // Handle multiple parameters for multi-select filters
          const itemIds = urlParams.getAll(filterType);
          if (itemIds.length > 0) {
            // Check if 'all' is present in URL for multi-select
            if (itemIds.includes('all')) {
              initValues[filterType] = 'all';
              saveFilter(filterType, 'all');
            } else {
              initValues[filterType] = itemIds;
              // Save item IDs to localStorage
              saveFilter(filterType, itemIds);
            }
          } else {
            // Not in URL - only restore if value is 'all' (indicates persistAll=true)
            if (storedFilters[filterType] === 'all') {
              initValues[filterType] = storedFilters[filterType];
            } else {
              clearFilter(filterType);
            }
          }
        } else {
          const urlValue = urlParams.get(filterType);
          if (urlValue) {
            initValues[filterType] = urlValue;
            // Save URL value to localStorage
            saveFilter(filterType, urlValue);
          }
          // If filter not in URL, only restore if value is 'all' (indicates persistAll=true)
          else {
            if (storedFilters[filterType] === 'all') {
              initValues[filterType] = storedFilters[filterType];
            } else {
              clearFilter(filterType);
            }
          }
        }
      });
    } else {
      // No URL filter parameters - use localStorage as fallback
      Object.keys(config.filters).forEach(filterType => {
        if (storedFilters[filterType]) {
          initValues[filterType] = storedFilters[filterType];
        }
      });
    }
    
    return initValues;
  }, [loadFilters, saveFilter, clearFilter]);

  return {
    saveFilter,
    clearFilter,
    loadFilters,
    updateUrlAndStorage,
    getInitialValues,
    persistenceConfig
  };
}; 
